import React from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import {
  FormModeToggle,
  FormAlerts,
  FormSubmitRow,
  DropdownField,
  ThemedSelect,
  RepeatableFormSection
} from '../../common';
import { useProductsForm } from './useProductsForm';
import { CONDITION_OPTIONS, WARRANTY_OPTIONS } from './productFormUtils';
import ProductPicturesSection from './ProductPicturesSection';
import { useAppStore } from '../../../store/appStore';

const ProductsForm = () => {
  const insertBasicData = useAppStore((s) => s.insertBasicData);
  const isAnySectionLoading = Object.values(insertBasicData.sectionLoading || {}).some(Boolean);
  const {
    mode,
    formData,
    formError,
    formSuccess,
    submitting,
    deleting,
    selectedProduct,
    productSearchQuery,
    showProductDropdown,
    productsLoading,
    categorySearchQuery,
    subCategorySearchQuery,
    showCategoryDropdown,
    showSubCategoryDropdown,
    categoriesLoading,
    filteredProducts,
    filteredCategoryOptions,
    filteredSubCategoryOptions,
    productDropdownRef,
    categoryDropdownRef,
    subCategoryDropdownRef,
    handleModeChange,
    handleChange,
    handleProductSearchChange,
    handleSelectProduct,
    handleCategoryFocus,
    handleSelectCategory,
    handleSubCategoryFocus,
    handleSelectSubCategory,
    handleSubmit,
    handleDelete,
    isEditMode,
    setCategorySearchQuery,
    setSubCategorySearchQuery,
    setFormData,
    setProductNameFieldTouched,
    setShowProductDropdown,
    setShowCategoryDropdown,
    setShowSubCategoryDropdown,
    additionalDetails,
    additionalDetailsLoading,
    addAdditionalDetail,
    updateAdditionalDetail,
    removeAdditionalDetail,
    loadAttributesFromCategory,
    attributesFromCategoryLoading,
    hasCategoryForAttributes,
    detailNameDropdownRef,
    openDetailNameDropdownIndex,
    setOpenDetailNameDropdownIndex,
    pictureDisplayItems,
    productPicturesLoading,
    pictureFileError,
    addPictures,
    removePicture,
    productSuppliers,
    productSuppliersLoading,
    suppliers,
    addProductSupplierLink,
    updateProductSupplierLink,
    removeProductSupplierLink,
    supplierDropdownRef,
    openSupplierDropdownIndex,
    setOpenSupplierDropdownIndex
  } = useProductsForm();

  return (
    <>
      <FormModeToggle mode={mode} onModeChange={handleModeChange} />
      <FormAlerts error={formError} success={formSuccess} />

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Product Name <span className="text-danger">*</span></Form.Label>
          {isEditMode ? (
            <DropdownField
              ref={productDropdownRef}
              value={productSearchQuery}
              onChange={handleProductSearchChange}
              placeholder="Search by product name..."
              disabled={productsLoading || isAnySectionLoading}
              loading={productsLoading}
              loadingMessage="Loading products..."
              showDropdown={showProductDropdown && !productsLoading && !isAnySectionLoading}
              options={filteredProducts}
              emptyMessage={productSearchQuery.trim() ? 'No matching products' : 'Type to search products'}
              getLabel={(p) => p.name || '(Unnamed)'}
              getItemKey={(p) => p.id}
              onSelect={handleSelectProduct}
              onFocus={() => {
                setProductNameFieldTouched(true);
                setShowProductDropdown(true);
              }}
              onClick={() => setShowProductDropdown(true)}
              id="product-dropdown"
            />
          ) : (
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter product name"
              required
            />
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Brand</Form.Label>
          <Form.Control type="text" name="brand" value={formData.brand} onChange={handleChange} placeholder="Enter brand" />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Product model</Form.Label>
          <Form.Control type="text" name="product_model" value={formData.product_model} onChange={handleChange} placeholder="Enter product model" />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control as="textarea" rows={3} name="description" value={formData.description} onChange={handleChange} placeholder="Enter product description" />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>SKU</Form.Label>
          <Form.Control type="text" name="sku" value={formData.sku} onChange={handleChange} placeholder="Stock Keeping Unit" />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>NCM</Form.Label>
          <Form.Control type="text" name="NCM" value={formData.NCM} onChange={handleChange} placeholder="Nomenclatura Comum do Mercosul" />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Product universal code</Form.Label>
          <Form.Control type="text" name="product_universal_code" value={formData.product_universal_code} onChange={handleChange} placeholder="EAN, UPC, etc." />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Category</Form.Label>
          <DropdownField
            ref={categoryDropdownRef}
            value={formData.category_id != null ? formData.categoryDisplayName : categorySearchQuery}
            onChange={(e) => {
              setCategorySearchQuery(e.target.value);
              setFormData((prev) => ({ ...prev, category_id: null, sub_category_id: null, categoryDisplayName: '', subCategoryDisplayName: '' }));
              setShowCategoryDropdown(true);
            }}
            placeholder="Search by category..."
            disabled={categoriesLoading || isAnySectionLoading}
            loading={categoriesLoading}
            loadingMessage="Loading categories..."
            showDropdown={showCategoryDropdown && !categoriesLoading && !isAnySectionLoading}
            options={filteredCategoryOptions}
            emptyMessage={categorySearchQuery.trim() ? 'No matching categories' : 'Type to search categories'}
            getLabel={(row) => row.category_name || '(Unnamed)'}
            getItemKey={(item) => `${item.id}-${item.sub_category_id}`}
            onSelect={handleSelectCategory}
            onFocus={handleCategoryFocus}
            onClick={() => setShowCategoryDropdown(true)}
            id="category-dropdown"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Sub Category</Form.Label>
          <DropdownField
            ref={subCategoryDropdownRef}
            value={formData.sub_category_id != null ? formData.subCategoryDisplayName : subCategorySearchQuery}
            onChange={(e) => {
              setSubCategorySearchQuery(e.target.value);
              setFormData((prev) => ({ ...prev, sub_category_id: null, subCategoryDisplayName: '' }));
              setShowSubCategoryDropdown(true);
            }}
            placeholder={formData.category_id == null ? 'Select a category first' : 'Search by sub-category...'}
            disabled={categoriesLoading || formData.category_id == null || isAnySectionLoading}
            loading={categoriesLoading}
            loadingMessage="Loading categories..."
            showDropdown={showSubCategoryDropdown && formData.category_id != null && !categoriesLoading && !isAnySectionLoading}
            options={filteredSubCategoryOptions}
            emptyMessage={subCategorySearchQuery.trim() ? 'No matching sub-categories' : 'Type to search sub-categories'}
            getLabel={(row) => row.sub_category_name || '(Unnamed)'}
            getItemKey={(item) => `${item.id}-${item.sub_category_id}`}
            onSelect={handleSelectSubCategory}
            onFocus={handleSubCategoryFocus}
            onClick={() => setShowSubCategoryDropdown(true)}
            id="sub-category-dropdown"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Condition</Form.Label>
          <ThemedSelect
            name="condition"
            value={formData.condition}
            options={CONDITION_OPTIONS}
            onChange={handleChange}
            id="condition-select"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Warranty</Form.Label>
          <ThemedSelect
            name="warranty"
            value={formData.warranty}
            options={WARRANTY_OPTIONS}
            onChange={handleChange}
            id="warranty-select"
          />
        </Form.Group>

        <RepeatableFormSection
          title="Suppliers"
          items={productSuppliers}
          getItemKey={(item, index) => item.id ?? `new-ps-${index}`}
          onAdd={addProductSupplierLink}
          onRemove={removeProductSupplierLink}
          loading={productSuppliersLoading}
          isEditMode={isEditMode}
          loadingMessage="Loading product suppliers..."
          addButtonLabel="+ Add supplier"
          addButtonTitle="Add product–supplier link"
          addButtonDisabled={isAnySectionLoading}
          removeDisabled={isAnySectionLoading}
        >
          {(item, index) => (
            <>
              <div
                className="product-supplier-fields-grid"
                style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem 0.75rem', flex: 1, alignContent: 'start' }}
              >
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-0">Supplier <span className="text-danger">*</span></Form.Label>
                  <div ref={openSupplierDropdownIndex === index ? supplierDropdownRef : null} className="position-relative">
                    <DropdownField
                      value={suppliers.find((s) => s.id === item.supplier_id)?.name ?? ''}
                      onChange={() => {}}
                      placeholder="Select supplier"
                      showDropdown={openSupplierDropdownIndex === index}
                      options={suppliers}
                      emptyMessage="No suppliers"
                      getLabel={(s) => s.name || '(Unnamed)'}
                      getItemKey={(s) => s.id}
                      onSelect={(s) => {
                        updateProductSupplierLink(index, 'supplier_id', s?.id ?? null);
                        setOpenSupplierDropdownIndex(null);
                      }}
                      onFocus={() => setOpenSupplierDropdownIndex(index)}
                      onClick={() => setOpenSupplierDropdownIndex(index)}
                      id={`product-supplier-${index}`}
                      size="sm"
                    />
                  </div>
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-0">Origin</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="Origin"
                    value={item.origin ?? ''}
                    onChange={(e) => updateProductSupplierLink(index, 'origin', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-0">Unit price</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    step="any"
                    placeholder="—"
                    value={item.unit_price ?? ''}
                    onChange={(e) => updateProductSupplierLink(index, 'unit_price', e.target.value === '' ? null : e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-0">Unit curr.</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="BRL"
                    value={item.unit_price_currency ?? ''}
                    onChange={(e) => updateProductSupplierLink(index, 'unit_price_currency', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-0">MOQ</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    placeholder="—"
                    value={item.moq ?? ''}
                    onChange={(e) => updateProductSupplierLink(index, 'moq', e.target.value === '' ? null : e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-0">Location</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="Storage"
                    value={item.stored_location ?? ''}
                    onChange={(e) => updateProductSupplierLink(index, 'stored_location', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-0">Available</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    step="any"
                    placeholder="—"
                    value={item.available_amount ?? ''}
                    onChange={(e) => updateProductSupplierLink(index, 'available_amount', e.target.value === '' ? null : e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-0">Sale price</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    step="any"
                    placeholder="—"
                    value={item.default_sale_price ?? ''}
                    onChange={(e) => updateProductSupplierLink(index, 'default_sale_price', e.target.value === '' ? null : e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-0">Sale curr.</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="BRL"
                    value={item.sale_price_currency ?? ''}
                    onChange={(e) => updateProductSupplierLink(index, 'sale_price_currency', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-0 d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    id={`product-supplier-shared-stock-${index}`}
                    checked={!!item.shared_stock}
                    onChange={(e) => updateProductSupplierLink(index, 'shared_stock', e.target.checked)}
                    label="Shared stock"
                    className="small mb-0"
                  />
                </Form.Group>
                <Form.Group className="mb-0 d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    id={`product-supplier-active-${index}`}
                    checked={item.active !== false}
                    onChange={(e) => updateProductSupplierLink(index, 'active', e.target.checked)}
                    label="Active"
                    className="small mb-0"
                  />
                </Form.Group>
              </div>
            </>
          )}
        </RepeatableFormSection>

        <h6 className="mb-3">Product pictures</h6>
        <ProductPicturesSection
          displayItems={pictureDisplayItems}
          onRemove={removePicture}
          onFileSelect={addPictures}
          fileError={pictureFileError}
          loading={isEditMode && productPicturesLoading}
          disabled={isAnySectionLoading}
        />
        <RepeatableFormSection
          title="Additional Details"
          headerAction={
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              onClick={loadAttributesFromCategory}
              disabled={!hasCategoryForAttributes || attributesFromCategoryLoading}
              title={
                hasCategoryForAttributes
                  ? 'Fetch category attributes and add rows'
                  : 'Select category and sub-category first'
              }
              className="d-flex align-items-center gap-1"
            >
              {attributesFromCategoryLoading ? (
                <>
                  <Spinner animation="border" role="status" size="sm" aria-hidden="true" />
                  <span>Loading…</span>
                </>
              ) : (
                'Load MLB Attributes'
              )}
            </Button>
          }
          items={additionalDetails}
          getItemKey={(item, index) => item.id ?? `new-${index}`}
          onAdd={addAdditionalDetail}
          onRemove={removeAdditionalDetail}
          loading={additionalDetailsLoading}
          isEditMode={isEditMode}
          loadingMessage="Loading additional details..."
          addButtonLabel="+"
          addButtonTitle="Add additional detail"
          addButtonDisabled={isAnySectionLoading}
          removeDisabled={isAnySectionLoading}
        >
          {(item, index) => {
            const hasAllowedValues = item.allowed_values?.length > 0;
            const detailNameOptions = hasAllowedValues
              ? (item.allowed_values || []).filter(
                  (v) =>
                    !(item.detail_name || '').trim() ||
                    (v.value_name || '')
                      .toLowerCase()
                      .includes((item.detail_name || '').toLowerCase())
                )
              : [];
            return (
              <>
                <Form.Group className="flex-grow-1" style={{ minWidth: '120px' }}>
                  <Form.Control
                    type="text"
                    placeholder="Attribute"
                    value={item.attribute_name ?? ''}
                    readOnly
                    size="sm"
                  />
                </Form.Group>
                <Form.Group className="flex-grow-1" style={{ minWidth: '120px' }}>
                  <Form.Control
                    type="text"
                    placeholder="E.g. Technical Specifications"
                    value={item.detail_group ?? ''}
                    onChange={(e) => updateAdditionalDetail(index, 'detail_group', e.target.value)}
                    size="sm"
                  />
                </Form.Group>
                <Form.Group className="flex-grow-1" style={{ minWidth: '120px' }}>
                  {hasAllowedValues ? (
                    <div
                      ref={openDetailNameDropdownIndex === index ? detailNameDropdownRef : null}
                      className="position-relative"
                    >
                      <DropdownField
                        value={item.detail_name ?? ''}
                        onChange={(e) => updateAdditionalDetail(index, 'detail_name', e.target.value)}
                        placeholder="Detail name (search or type)"
                        showDropdown={openDetailNameDropdownIndex === index}
                        options={detailNameOptions}
                        emptyMessage={
                          (item.detail_name || '').trim()
                            ? 'No matching values'
                            : 'Type to search or pick a value'
                        }
                        getLabel={(v) => v.value_name ?? ''}
                        getItemKey={(v) => v.value_id ?? v.value_name}
                        onSelect={(v) => {
                          updateAdditionalDetail(index, 'detail_name', v.value_name ?? '');
                          setOpenDetailNameDropdownIndex(null);
                        }}
                        onFocus={() => setOpenDetailNameDropdownIndex(index)}
                        onClick={() => setOpenDetailNameDropdownIndex(index)}
                        id={`detail-name-dropdown-${index}`}
                        size="sm"
                      />
                    </div>
                  ) : (
                    <Form.Control
                      type="text"
                      placeholder="Detail name"
                      value={item.detail_name ?? ''}
                      onChange={(e) => updateAdditionalDetail(index, 'detail_name', e.target.value)}
                      size="sm"
                    />
                  )}
                </Form.Group>
                <Form.Group className="flex-grow-1" style={{ minWidth: '120px' }}>
                  <Form.Control
                    type="text"
                    placeholder="Detail value"
                    value={item.detail_value ?? ''}
                    onChange={(e) => updateAdditionalDetail(index, 'detail_value', e.target.value)}
                    size="sm"
                  />
                </Form.Group>
              </>
            );
          }}
        </RepeatableFormSection>

        <FormSubmitRow
          isEditMode={isEditMode}
          submitting={submitting}
          deleting={deleting}
          onDelete={handleDelete}
          submitLabel="Submit changes"
          deleteTitle="Delete product"
          disabled={!selectedProduct?.id || isAnySectionLoading}
          createLabel="Create Product"
        />
      </Form>
    </>
  );
};

export default ProductsForm;
